import { ReactElement, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Text, TextTag, ThemeProvider } from '@volkswagen-onehub/components-core';
import { View360 } from '@volkswagen-onehub/icons-core';

import RotateDevicePrompt from '@/components/rotateDevicePrompt/RotateDevicePrompt';
import { useTrackingService } from '@/contexts/TrackingServiceContext';
import useBreakpoint from '@/hooks/useBreakpoint';
import { useButtonSize } from '@/hooks/useButtonSize';
import useDeviceOrientation from '@/hooks/useDeviceOrientation';
import { TrackingEvent } from '@/util/tracking/definitions';

import WebStreaming from './WebStreaming';
import {
  DeckOnExceptionEventDetail,
  StreamingErrorReasonsSet,
  StreamingState,
} from './WebStreaming.types';
import {
  StyledErrorButton,
  StyledErrorContainer,
  StyledInnerErrorContainer,
  StyledTextButton,
  StyledWebStreamingOverlay,
  StyledWebStreamingWrapper,
} from './WebstreamingOverlay.styles';

type WebStreamingOverlayProps = {
  shouldGuardMobileLandscape?: boolean;
};

const WebStreamingOverlay = ({
  shouldGuardMobileLandscape = false,
}: WebStreamingOverlayProps): ReactElement => {
  const { t } = useTranslation();
  const trackingService = useTrackingService();
  const buttonSize = useButtonSize();

  const { isPhone } = useBreakpoint();
  const { isPortrait, isLandscape } = useDeviceOrientation();

  const [active, setActive] = useState<boolean>(false);
  const [streamingState, setStreamingState] = useState<StreamingState>(StreamingState.INITIAL);
  const [noInstancesAvailable, setNoInstancesAvailable] = useState(false);
  const [showRotateDevicePrompt, setShowRotateDevicePrompt] = useState(false);

  const showStartButton =
    [StreamingState.INITIAL, StreamingState.STOPPED].includes(streamingState) &&
    !noInstancesAvailable &&
    !showRotateDevicePrompt;

  const showStreamingOverlay =
    showRotateDevicePrompt ||
    [StreamingState.LOADING, StreamingState.CONNECTED, StreamingState.ERROR].includes(
      streamingState,
    ) ||
    noInstancesAvailable;

  const showError = streamingState === StreamingState.ERROR || noInstancesAvailable;

  const startStream = useCallback(() => {
    setActive(true);
    setNoInstancesAvailable(false);
    setStreamingState(StreamingState.LOADING);
  }, []);

  const onConnected = () => {
    setStreamingState(StreamingState.CONNECTED);
  };

  const onException = (error: CustomEvent<DeckOnExceptionEventDetail>) => {
    const errorReason = error?.detail?.reason;

    console.error(`[DGA Customer UI - Error Reason]: ${error?.detail.reason}`);

    if (errorReason && StreamingErrorReasonsSet.has(errorReason)) {
      setNoInstancesAvailable(true);
    }

    setActive(false);
    setShowRotateDevicePrompt(false);
    setStreamingState(StreamingState.ERROR);
  };

  const onDisconnected = () => {
    setActive(false);
    setShowRotateDevicePrompt(false);
    setStreamingState(StreamingState.STOPPED);
  };

  const onStreamInitialized = () => {
    setStreamingState(StreamingState.CONNECTED);
  };

  const onStartStream = () => {
    const STREAMING_CTA_NAME = '3D Streaming';

    trackingService.track(TrackingEvent.VWBasicCTAClick, {
      link: {
        name: STREAMING_CTA_NAME,
      },
      process: {
        StepName: STREAMING_CTA_NAME,
        StepNumber: '1',
      },
      eventInfo: {
        contentId: STREAMING_CTA_NAME,
      },
    });

    if (shouldGuardMobileLandscape && isPhone && isPortrait) {
      setActive(false);
      setNoInstancesAvailable(false);
      setShowRotateDevicePrompt(true);

      return;
    }

    startStream();
  };

  const onBackFromError = () => {
    setActive(false);
    setShowRotateDevicePrompt(false);
    setStreamingState(StreamingState.STOPPED);
    setNoInstancesAvailable(false);
  };

  useEffect(() => {
    if (!showRotateDevicePrompt) {
      return;
    }

    if (!isPhone || isLandscape) {
      setShowRotateDevicePrompt(false);
      startStream();
    }
  }, [isPhone, isLandscape, showRotateDevicePrompt, startStream]);

  const streamingOverlay = showStreamingOverlay ? (
    <ThemeProvider theme="main" variant="v26">
      <StyledWebStreamingOverlay data-testid="webstreaming-overlay">
        {showRotateDevicePrompt ? (
          <RotateDevicePrompt
            message={t('webstreaming.rotateDevicePrompt', {
              defaultValue: 'For best experience, please rotate your device!',
            })}
          />
        ) : (
          <StyledWebStreamingWrapper>
            <WebStreaming
              active={active}
              connected={onConnected}
              exception={onException}
              disconnected={onDisconnected}
              initialized={onStreamInitialized}
            />
          </StyledWebStreamingWrapper>
        )}

        {showError && (
          <StyledErrorContainer>
            <StyledInnerErrorContainer>
              <Text
                tag={TextTag.p}
                aria-describedby={t('webstreaming.failureNotification.ariaLabel')}
              >
                {t('webstreaming.failureNotification.text')}
              </Text>

              <StyledErrorButton
                tag="button"
                size="small"
                aria-label={t('webstreaming.failureNotification.backButton.ariaLabel')}
                onClick={onBackFromError}
              >
                {t('webstreaming.failureNotification.backButton.title')}
              </StyledErrorButton>
            </StyledInnerErrorContainer>
          </StyledErrorContainer>
        )}
      </StyledWebStreamingOverlay>
    </ThemeProvider>
  ) : null;

  return (
    <ThemeProvider theme="inverted">
      <>
        {showStartButton && (
          <StyledTextButton
            icon={<View360 aria-hidden />}
            tag="button"
            size={buttonSize}
            aria-label={t('webstreaming.startButton.ariaLabel')}
            title={t('webstreaming.startButton.title')}
            onClick={onStartStream}
            data-testid="start-webstreaming"
          >
            {t('webstreaming.startButton.title')}
          </StyledTextButton>
        )}

        {typeof document !== 'undefined' && streamingOverlay
          ? createPortal(streamingOverlay, document.body)
          : null}
      </>
    </ThemeProvider>
  );
};

export default WebStreamingOverlay;
